import React, { useState, useEffect } from 'react';
import { useAuth, apiClient } from '../auth/AuthContext';
import { useNotification } from '../shared/NotificationContext';
import DayMonthDateInput from '../shared/DayMonthDateInput';
import { localDateToIso } from '../../shared/date';

interface AccessRequest {
  id: string;
  buildingId: string;
  tenantId: string;
  requesterUserId: string;
  startDateTime: string;
  endDateTime: string;
  timezone?: string;
  areas?: string[];
  reason?: string;
  status: 'PENDING' | 'APPROVED' | 'DENIED' | 'CANCELLED';
  decision?: {
    decisionBy: string;
    decisionAt: string;
    reason?: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface User {
  id: string;
  username: string;
  email: string;
  role: 'admin' | 'manager' | 'tenant';
  buildingId: string;
}

interface RequestViewProps {
  initialFilters?: {
    area?: string;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
  };
}

const RequestViewPage: React.FC<RequestViewProps> = ({ initialFilters = {} }) => {
  const { user, token } = useAuth();
  const { notify } = useNotification();
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [users, setUsers] = useState<{[userId: string]: User}>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [requestToDelete, setRequestToDelete] = useState<AccessRequest | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  // Filter states
  const [searchQuery, setSearchQuery] = useState(initialFilters.search || '');
  const [areaFilter, setAreaFilter] = useState(initialFilters.area || '');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateRange, setDateRange] = useState(() => {
    if (initialFilters.dateFrom && initialFilters.dateTo) {
      return {
        from: initialFilters.dateFrom,
        to: initialFilters.dateTo
      };
    }
    
    // Default to current month
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    return {
      from: localDateToIso(firstDay),
      to: localDateToIso(lastDay),
    };
  });

  const fetchRequests = async () => {
    if (!token || !user?.buildingId) return;

    setLoading(true);
    setError('');

    try {
      const fromD = new Date(dateRange.from + 'T00:00:00Z');
      const toD = new Date(dateRange.to + 'T23:59:59Z');
      const params = new URLSearchParams({
        page: currentPage.toString(),
        pageSize: pageSize.toString(),
        buildingId: user.buildingId,
        from: String(fromD.getTime()),
        to: String(toD.getTime()),
      });

      if (statusFilter) {
        params.append('status', statusFilter);
      }

      const result: any = await apiClient.get(`/requests?${params.toString()}`);
      
      let filteredRequests: AccessRequest[] = result.data || [];
      
      // Apply client-side filters for area and search
      if (areaFilter) {
        filteredRequests = filteredRequests.filter((req: AccessRequest) => 
          req.areas?.some(area => area.toLowerCase().includes(areaFilter.toLowerCase()))
        );
      }
      
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filteredRequests = filteredRequests.filter((req: AccessRequest) => 
          req.id.toLowerCase().includes(query) ||
          req.areas?.some(area => area.toLowerCase().includes(query)) ||
          req.requesterUserId.toLowerCase().includes(query) ||
          (req.reason && req.reason.toLowerCase().includes(query))
        );
      }

      setRequests(filteredRequests);
      setTotalItems(result.meta?.totalItems || filteredRequests.length);

      // Fetch user details for unique user IDs
      const uniqueUserIds = Array.from(new Set(filteredRequests.map((req: AccessRequest) => req.requesterUserId)));
      await fetchUserDetails(uniqueUserIds);

    } catch (err: any) {
      setError(err.message || 'Failed to fetch requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (userIds: string[]) => {
    try {
      // For now, we'll create mock user data since there might not be a users endpoint
      // In a real implementation, you'd fetch from /api/users
      const mockUsers: {[key: string]: User} = {};
      userIds.forEach(userId => {
        mockUsers[userId] = {
          id: userId,
          username: userId,
          email: `${userId}@company.com`,
          role: 'tenant',
          buildingId: user?.buildingId || ''
        };
      });
      setUsers(prev => ({ ...prev, ...mockUsers }));
    } catch (err) {
      console.error('Error fetching user details:', err);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [currentPage, statusFilter, dateRange, token, user?.buildingId]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchRequests();
  };

  const getStatusBadge = (status: string) => {
    const statusColors = {
      PENDING: '#f6e05e',
      APPROVED: '#68d391',
      DENIED: '#f56565',
      CANCELLED: '#a0aec0'
    };
    
    return (
      <span style={{ 
        padding: '4px 8px', 
        borderRadius: 4, 
        backgroundColor: statusColors[status as keyof typeof statusColors] || '#e2e8f0',
        color: 'white',
        fontSize: 12,
        fontWeight: 600
      }}>
        {status}
      </span>
    );
  };

  const formatDateTime = (dateTime: string | number) => {
    const ms = typeof dateTime === 'number' ? dateTime : (Number(dateTime) || Date.parse(dateTime));
    return new Date(ms).toLocaleString();
  };

  const calculateDuration = (start: string | number, end: string | number) => {
    const s = typeof start === 'number' ? start : (Number(start) || Date.parse(start));
    const e = typeof end === 'number' ? end : (Number(end) || Date.parse(end));
    const hours = (e - s) / (1000 * 60 * 60);
    return `${hours.toFixed(1)}h`;
  };

  const openDeleteModal = (request: AccessRequest) => {
    setError('');
    setRequestToDelete(request);
  };

  const closeDeleteModal = () => {
    if (!deleting) {
      setRequestToDelete(null);
    }
  };

  const confirmDeleteRequest = async () => {
    if (!requestToDelete) return;

    setDeleting(true);
    setError('');

    try {
      await apiClient.delete(`/requests/${requestToDelete.id}`);

      setRequests(prev => prev.filter(req => req.id !== requestToDelete.id));
      setTotalItems(prev => Math.max(0, prev - 1));
      setRequestToDelete(null);
      notify('Request deleted successfully.', 'success');
    } catch (err: any) {
      console.error('Error deleting request:', err);
      notify(err.message || 'Failed to delete request', 'error');
      setRequestToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="ah-shell">
      <div className="ah-panel">
        <div style={{ maxWidth: 1400, margin: '0 auto' }}>
          <div className="ah-card">
            <div style={{ marginBottom: 24 }}>
              <h1 className="ah-title">Request Management</h1>
              <p className="muted">View and manage all after hours requests for your building</p>
            </div>

            {/* Filters */}
            <div className="ah-card" style={{ marginBottom: 24 }}>
              <h3 style={{ marginBottom: 16 }}>Filters</h3>
              <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: 16 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>Search</label>
                  <input
                    type="text"
                    className="ah-input"
                    placeholder="Search by ID, area, user, or reason..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>Area</label>
                  <input
                    type="text"
                    className="ah-input"
                    placeholder="Filter by area..."
                    value={areaFilter}
                    onChange={(e) => setAreaFilter(e.target.value)}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>Status</label>
                  <select 
                    className="ah-input"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">All Statuses</option>
                    <option value="PENDING">Pending</option>
                    <option value="APPROVED">Approved</option>
                    <option value="DENIED">Denied</option>
                    <option value="CANCELLED">Cancelled</option>
                  </select>
                </div>
              </div>
              
              <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', marginBottom: 16 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>From Date</label>
                  <DayMonthDateInput
                    value={dateRange.from}
                    onChange={(from) => setDateRange(prev => ({ ...prev, from }))}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>To Date</label>
                  <DayMonthDateInput
                    value={dateRange.to}
                    onChange={(to) => setDateRange(prev => ({ ...prev, to }))}
                  />
                </div>
              </div>
              
              <button onClick={handleSearch} className="ah-btn">
                Apply Filters
              </button>
            </div>

            {/* Loading State */}
            {loading && (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <div>Loading requests...</div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div style={{ 
                color: '#e53e3e', 
                backgroundColor: '#fed7d7', 
                border: '1px solid #feb2b2', 
                borderRadius: 6, 
                padding: 16, 
                marginBottom: 24 
              }}>
                {error}
              </div>
            )}

            {/* Results */}
            {!loading && requests.length > 0 && (
              <>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="muted">
                    Showing {requests.length} of {totalItems} requests
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button 
                      className="ah-btn ah-btn-outline" 
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    >
                      Previous
                    </button>
                    <span style={{ display: 'flex', alignItems: 'center', padding: '0 12px' }}>
                      Page {currentPage}
                    </span>
                    <button 
                      className="ah-btn ah-btn-outline" 
                      disabled={requests.length < pageSize}
                      onClick={() => setCurrentPage(prev => prev + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>

                <div className="ah-table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Request ID</th>
                        <th>User</th>
                        <th>Date & Time</th>
                        <th>Duration</th>
                        <th>Areas</th>
                        <th>Reason</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requests.map((request) => (
                        <tr key={request.id}>
                          <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                            {request.id.substring(0, 8)}...
                          </td>
                          <td>
                            <div>
                              <div style={{ fontWeight: 500 }}>{users[request.requesterUserId]?.username || request.requesterUserId}</div>
                              <div className="muted" style={{ fontSize: 12 }}>
                                {users[request.requesterUserId]?.email || `${request.requesterUserId}@company.com`}
                              </div>
                            </div>
                          </td>
                          <td>
                            <div style={{ fontSize: 12 }}>
                              <div>{formatDateTime(request.startDateTime)}</div>
                              <div className="muted">to {formatDateTime(request.endDateTime)}</div>
                            </div>
                          </td>
                          <td>{calculateDuration(request.startDateTime, request.endDateTime)}</td>
                          <td>
                            <div style={{ fontSize: 12 }}>
                              {request.areas?.join(', ') || 'No areas'}
                            </div>
                          </td>
                          <td>
                            <div style={{ maxWidth: 200, fontSize: 12 }}>
                              {request.reason || 'No reason provided'}
                            </div>
                          </td>
                          <td>{getStatusBadge(request.status)}</td>
                          <td>
                            <button 
                              className="ah-btn ah-btn-outline ah-btn-primary" 
                              style={{ fontSize: 12, padding: '4px 8px' }}
                              onClick={() => {
                                // Navigate to request detail - implement as needed
                                console.log('View request details:', request.id);
                              }}
                            >
                              View
                            </button>
                            
                            <button 
                              className="ah-btn ah-btn-outline ah-btn-danger" 
                              style={{ fontSize: 12, padding: '4px 8px' }}
                              onClick={() => openDeleteModal(request)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* No Results State */}
            {!loading && requests.length === 0 && !error && (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <div className="muted">No requests found matching your criteria.</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {requestToDelete && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-request-title"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            zIndex: 1000,
          }}
          onClick={closeDeleteModal}
        >
          <div
            className="ah-card"
            style={{ width: '100%', maxWidth: 520 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="delete-request-title" style={{ marginBottom: 8 }}>Delete request?</h2>
            <p className="muted" style={{ marginBottom: 20 }}>
              This action cannot be undone.
            </p>

            <div style={{ display: 'grid', gap: 12, marginBottom: 24, fontSize: 14 }}>
              <div>
                <strong>Request ID:</strong>{' '}
                <span style={{ fontFamily: 'monospace' }}>{requestToDelete.id}</span>
              </div>
              <div>
                <strong>User:</strong>{' '}
                {users[requestToDelete.requesterUserId]?.username || requestToDelete.requesterUserId}
              </div>
              <div>
                <strong>Date:</strong> {formatDateTime(requestToDelete.startDateTime)}
              </div>
              <div>
                <strong>Duration:</strong>{' '}
                {calculateDuration(requestToDelete.startDateTime, requestToDelete.endDateTime)}
              </div>
              <div>
                <strong>Areas:</strong> {requestToDelete.areas?.join(', ') || 'No areas'}
              </div>
              <div>
                <strong>Reason:</strong> {requestToDelete.reason || 'No reason provided'}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                className="ah-btn ah-btn-outline"
                onClick={closeDeleteModal}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ah-btn ah-btn-danger"
                onClick={confirmDeleteRequest}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestViewPage;
